import { useCallback, useMemo } from "react"
import {
    useNodesState,
    useEdgesState,
    addEdge as rfAddEdge,
    type Connection,
    type NodeChange,
    type EdgeChange
} from "@xyflow/react"
import type { BaseNodeData, BaseEdgeData, BaseNode, BaseEdge, UseGraphStateReturn, UseGraphStateOptions } from "../types"

/**
 * Generic hook for managing graph state with React Flow.
 * Provides CRUD operations and selection tracking.
 */
export function useGraphState<TNodeData extends BaseNodeData = BaseNodeData, TEdgeData extends BaseEdgeData = BaseEdgeData>(
    options: UseGraphStateOptions<TNodeData, TEdgeData> = {}
): UseGraphStateReturn<TNodeData, TEdgeData> {
    const { initialNodes = [], initialEdges = [], onChange, defaultEdgeData } = options

    const [nodes, setNodes, onNodesChange] = useNodesState<BaseNode<TNodeData>>(initialNodes)
    const [edges, setEdges, onEdgesChange] = useEdgesState<BaseEdge<TEdgeData>>(initialEdges)

    const selectedNodeIds = useMemo(() => nodes.filter((n) => n.selected).map((n) => n.id), [nodes])

    const selectedEdgeIds = useMemo(() => edges.filter((e) => e.selected).map((e) => e.id), [edges])

    const handleNodesChange = useCallback(
        (changes: NodeChange<BaseNode<TNodeData>>[]) => {
            onNodesChange(changes)
        },
        [onNodesChange]
    )

    const handleEdgesChange = useCallback(
        (changes: EdgeChange<BaseEdge<TEdgeData>>[]) => {
            onEdgesChange(changes)
        },
        [onEdgesChange]
    )

    const onConnect = useCallback(
        (connection: Connection) => {
            const newEdge: BaseEdge<TEdgeData> = {
                id: `edge-${connection.source}-${connection.target}`,
                source: connection.source,
                target: connection.target,
                sourceHandle: connection.sourceHandle ?? undefined,
                targetHandle: connection.targetHandle ?? undefined,
                data: defaultEdgeData as TEdgeData
            }
            setEdges((eds) => rfAddEdge(newEdge, eds))
        },
        [setEdges, defaultEdgeData]
    )

    const addNode = useCallback(
        (node: BaseNode<TNodeData>) => {
            setNodes((nds) => [...nds, node])
            onChange?.([...nodes, node], edges as BaseEdge<TEdgeData>[])
        },
        [setNodes, nodes, edges, onChange]
    )

    const removeNode = useCallback(
        (nodeId: string) => {
            setNodes((nds) => nds.filter((n) => n.id !== nodeId))
            setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
            onChange?.(
                nodes.filter((n) => n.id !== nodeId),
                edges.filter((e) => e.source !== nodeId && e.target !== nodeId) as BaseEdge<TEdgeData>[]
            )
        },
        [setNodes, setEdges, nodes, edges, onChange]
    )

    const updateNode = useCallback(
        (nodeId: string, data: Partial<TNodeData>) => {
            setNodes((nds) =>
                nds.map((node) => {
                    if (node.id !== nodeId) return node
                    return { ...node, data: { ...node.data, ...data } }
                })
            )
        },
        [setNodes]
    )

    const addEdge = useCallback(
        (edge: BaseEdge<TEdgeData>) => {
            const newEdges = [...edges, edge]
            setEdges(newEdges)
            onChange?.(nodes, newEdges as BaseEdge<TEdgeData>[])
        },
        [setEdges, nodes, edges, onChange]
    )

    const updateEdge = useCallback(
        (edgeId: string, data: Partial<TEdgeData>) => {
            setEdges((eds) =>
                eds.map((edge) => {
                    if (edge.id !== edgeId) return edge
                    return { ...edge, data: { ...edge.data, ...data } as TEdgeData }
                })
            )
        },
        [setEdges]
    )

    const removeEdge = useCallback(
        (edgeId: string) => {
            const newEdges = edges.filter((e) => e.id !== edgeId)
            setEdges(newEdges)
            onChange?.(nodes, newEdges as BaseEdge<TEdgeData>[])
        },
        [setEdges, nodes, edges, onChange]
    )

    const removeSelected = useCallback(() => {
        const nodeIdsToRemove = new Set(selectedNodeIds)
        const edgeIdsToRemove = new Set(selectedEdgeIds)

        const newNodes = nodes.filter((n) => !nodeIdsToRemove.has(n.id))
        const newEdges = edges.filter((e) => {
            // Remove if edge is selected or if connected to a removed node
            if (edgeIdsToRemove.has(e.id)) return false
            if (nodeIdsToRemove.has(e.source) || nodeIdsToRemove.has(e.target)) return false
            return true
        })

        setNodes(newNodes)
        setEdges(newEdges)
        onChange?.(newNodes, newEdges as BaseEdge<TEdgeData>[])
    }, [selectedNodeIds, selectedEdgeIds, nodes, edges, setNodes, setEdges, onChange])

    const clearSelection = useCallback(() => {
        setNodes((nds) => nds.map((n) => ({ ...n, selected: false })))
        setEdges((eds) => eds.map((e) => ({ ...e, selected: false })))
    }, [setNodes, setEdges])

    return {
        nodes,
        edges,
        setNodes,
        setEdges,
        onNodesChange: handleNodesChange,
        onEdgesChange: handleEdgesChange,
        onConnect,
        selectedNodeIds,
        selectedEdgeIds,
        addNode,
        removeNode,
        updateNode,
        addEdge,
        updateEdge,
        removeEdge,
        removeSelected,
        clearSelection
    }
}
